import { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, PullToRefresh, Empty, DotLoading, Switch, Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  DRIVER_VISIBLE_TRIP_OR_FILTER,
  setAcceptingTasks,
  getCurrentPositionCoords,
} from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../hooks/useAuth';
import TaskCard from '../components/TaskCard';
import OfflineBanner from '../components/OfflineBanner';
import TaskHallHeader from '../components/TaskHallHeader';
import GPSManager from '../utils/GPSManager';
import { getDateFilter, type DateRange } from '../utils/tripUi';
import { fetchTodayShiftInspection } from '../utils/inspectionApi';
import { isFitnessPassing } from '@ubus/shared';

const TRIPS_CACHE_KEY = 'ubus-task-hall-cache';
const PENDING_STATUS_KEY = 'ubus-pending-status';

interface PendingStatusEntry {
  tripId: string;
  status: string;
}

async function syncPendingTripStatus(): Promise<void> {
  try {
    const raw = localStorage.getItem(PENDING_STATUS_KEY);
    if (!raw) return;

    const pending: PendingStatusEntry[] = JSON.parse(raw);
    if (pending.length === 0) return;

    const remaining: PendingStatusEntry[] = [];
    for (const entry of pending) {
      const { error } = await supabase
        .from('trips')
        .update({ status: entry.status, updated_at: new Date().toISOString() })
        .eq('id', entry.tripId);

      if (error) {
        remaining.push(entry);
      }
    }

    if (remaining.length === 0) {
      localStorage.removeItem(PENDING_STATUS_KEY);
    } else {
      localStorage.setItem(PENDING_STATUS_KEY, JSON.stringify(remaining));
    }
  } catch {
    // ignore
  }
}

function isOfflineModeEnabled(): boolean {
  return localStorage.getItem('ubus_offline_mode') === 'true';
}

function TodayTasks() {
  const { t } = useTranslation();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [allTrips, setAllTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [expandedTripId, setExpandedTripId] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine || isOfflineModeEnabled());
  const [inspectionReady, setInspectionReady] = useState<boolean | null>(null);
  const [acceptingTasks, setAcceptingTasksState] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [preferredVehicleId, setPreferredVehicleId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const locationKey = location.key;
  const { driverId } = useAuth();

  const hasOpenTripsToday = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    return allTrips.some(
      (t) =>
        t.date === today &&
        (t.status === 'scheduled' || t.status === 'dispatched' || t.status === 'in_progress')
    );
  }, [allTrips]);

  const hasFullDayEscortToday = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    return allTrips.some(
      (t) =>
        t.date === today &&
        Boolean(t.driver_full_day_escort) &&
        (t.status === 'scheduled' || t.status === 'dispatched' || t.status === 'in_progress')
    );
  }, [allTrips]);

  const canToggleAcceptOn =
    inspectionReady === true && !hasOpenTripsToday && !hasFullDayEscortToday;

  useEffect(() => {
    if (!driverId) {
      setInspectionReady(null);
      setAcceptingTasksState(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const insp = await fetchTodayShiftInspection(driverId);
      const { data: driverRow } = await supabase
        .from('drivers')
        .select('accepting_tasks, preferred_vehicle_id')
        .eq('id', driverId)
        .maybeSingle();
      if (!cancelled) {
        setInspectionReady(!!insp && isFitnessPassing(insp.work_fields?.fitness));
        setAcceptingTasksState(Boolean(driverRow?.accepting_tasks));
        setPreferredVehicleId((driverRow?.preferred_vehicle_id as string) ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [driverId, locationKey]);

  const resolveVehicleId = useCallback((): string | null => {
    if (preferredVehicleId) return preferredVehicleId;
    const today = dayjs().format('YYYY-MM-DD');
    const withVehicle = allTrips.find((t) => t.date === today && t.vehicle_id);
    return withVehicle?.vehicle_id ?? null;
  }, [preferredVehicleId, allTrips]);

  const handleAcceptToggle = async (checked: boolean) => {
    if (!driverId) return;
    if (checked && !canToggleAcceptOn) {
      if (hasFullDayEscortToday) {
        Toast.show({ content: t('hall.acceptBlockedFullDayEscort') });
      } else if (hasOpenTripsToday) {
        Toast.show({ content: t('hall.acceptBlockedOpenTrips') });
      } else {
        Toast.show({ content: t('hall.acceptBlockedInspection') });
      }
      return;
    }
    setAcceptBusy(true);
    try {
      let lat: number | undefined;
      let lng: number | undefined;
      if (checked) {
        const coords = await getCurrentPositionCoords();
        lat = coords.lat;
        lng = coords.lng;
      }
      const result = await setAcceptingTasks({
        accept: checked,
        vehicleId: resolveVehicleId(),
        lat,
        lng,
      });
      setAcceptingTasksState(result.accepting_tasks);
      Toast.show({
        icon: 'success',
        content: result.accepting_tasks ? t('hall.acceptOn') : t('hall.acceptOff'),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('hall.acceptFailed');
      if (msg.includes('GPS_REQUIRED') || msg.includes('Geolocation')) {
        Toast.show({ content: t('hall.acceptGpsRequired') });
      } else if (msg.includes('FULL_DAY_ESCORT')) {
        Toast.show({ content: t('hall.acceptBlockedFullDayEscort') });
      } else if (msg.includes('OPEN_TRIPS')) {
        Toast.show({ content: t('hall.acceptBlockedOpenTrips') });
      } else if (msg.includes('INSPECTION_REQUIRED')) {
        Toast.show({ content: t('hall.acceptBlockedInspection') });
      } else if (msg.includes('VEHICLE_REQUIRED')) {
        Toast.show({ content: t('hall.acceptVehicleRequired') });
      } else {
        Toast.show({ content: msg });
      }
    } finally {
      setAcceptBusy(false);
    }
  };

  const tabCounts = useMemo(() => {
    const today = getDateFilter('today');
    const tomorrow = getDateFilter('tomorrow');
    const future = getDateFilter('future');
    const countFor = (gte: string, lte: string | null) =>
      allTrips.filter((trip) => trip.date >= gte && (!lte || trip.date <= lte)).length;
    return {
      today: countFor(today.gte, today.lte),
      tomorrow: countFor(tomorrow.gte, tomorrow.lte),
      future: countFor(future.gte, future.lte),
    };
  }, [allTrips]);

  const saveTripsToCache = (data: Trip[], range: DateRange) => {
    try {
      const raw = localStorage.getItem(TRIPS_CACHE_KEY);
      const store = raw ? JSON.parse(raw) : {};
      store[range] = { trips: data, cachedAt: new Date().toISOString() };
      localStorage.setItem(TRIPS_CACHE_KEY, JSON.stringify(store));
    } catch {
      // ignore
    }
  };

  const loadTripsFromCache = (range: DateRange): Trip[] | null => {
    try {
      const raw = localStorage.getItem(TRIPS_CACHE_KEY);
      if (!raw) return null;
      const store = JSON.parse(raw);
      const cached = store[range];
      if (cached?.cachedAt && dayjs(cached.cachedAt).isSame(dayjs(), 'day')) {
        return cached.trips;
      }
    } catch {
      // ignore
    }
    return null;
  };

  const fetchAllTrips = useCallback(async () => {
    if (!driverId) return;
    const { gte } = getDateFilter('today');
    const { data } = await supabase
      .from('trips')
      .select('*, vehicles(*), organizations(*)')
      .eq('driver_id', driverId)
      .or(DRIVER_VISIBLE_TRIP_OR_FILTER)
      .gte('date', gte)
      .in('status', ['scheduled', 'dispatched', 'in_progress'])
      .order('date', { ascending: true })
      .order('pickup_time', { ascending: true });
    setAllTrips(data || []);
  }, [driverId]);

  const fetchTrips = useCallback(async () => {
    if (!driverId) {
      setLoading(false);
      return;
    }

    if (isOfflineModeEnabled() || !navigator.onLine) {
      const cached = loadTripsFromCache(dateRange);
      setTrips(cached || []);
      setIsOffline(true);
      setLoading(false);
      return;
    }

    setIsOffline(false);
    setLoading(true);

    try {
      const { gte, lte } = getDateFilter(dateRange);
      let query = supabase
        .from('trips')
        .select('*, vehicles(*), organizations(*)')
        .eq('driver_id', driverId)
        .or(DRIVER_VISIBLE_TRIP_OR_FILTER)
        .gte('date', gte)
        .in('status', ['scheduled', 'dispatched', 'in_progress'])
        .order('pickup_time', { ascending: true });

      if (lte) query = query.lte('date', lte);

      const { data, error } = await query;

      if (error) {
        const cached = loadTripsFromCache(dateRange);
        setTrips(cached || []);
        setIsOffline(true);
      } else {
        const list = data || [];
        setTrips(list);
        saveTripsToCache(list, dateRange);
        await fetchAllTrips();
      }
    } catch {
      const cached = loadTripsFromCache(dateRange);
      setTrips(cached || []);
      setIsOffline(true);
    } finally {
      setLoading(false);
    }
  }, [driverId, dateRange, fetchAllTrips]);

  useEffect(() => {
    const handleOnline = () => {
      if (!isOfflineModeEnabled()) {
        setIsOffline(false);
        void GPSManager.flushCachedReports();
        void syncPendingTripStatus();
        fetchTrips();
      }
    };
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchTrips]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const segments: { key: DateRange; label: string }[] = [
    { key: 'today', label: t('hall.today') },
    { key: 'tomorrow', label: t('hall.tomorrow') },
    { key: 'future', label: t('hall.future') },
  ];

  const emptyDescriptions: Record<DateRange, string> = {
    today: t('hall.emptyToday'),
    tomorrow: t('hall.emptyTomorrow'),
    future: t('hall.emptyFuture'),
  };

  return (
    <div className="driver-hall-page">
      <TaskHallHeader taskCount={tabCounts.today + tabCounts.tomorrow + tabCounts.future} />

      {inspectionReady === false && (
        <div
          style={{
            margin: '8px 16px 0',
            padding: '10px 12px',
            borderRadius: 12,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t('hall.inspectionNeeded')}</div>
          <Button color="primary" size="small" onClick={() => navigate('/inspection')}>
            {t('hall.inspect')}
          </Button>
        </div>
      )}

      {inspectionReady === true && (
        <div
          style={{
            margin: '6px 16px 0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            fontSize: 12,
          }}
        >
          <button
            type="button"
            style={{ border: 0, background: 'transparent', color: '#1677ff', padding: 0 }}
            onClick={() => navigate('/inspection')}
          >
            {t('hall.recheckVehicle')}
          </button>
          <button
            type="button"
            style={{ border: 0, background: 'transparent', color: '#1677ff', padding: 0 }}
            onClick={() => navigate('/inspection?mode=after')}
          >
            {t('hall.finishCheck')}
          </button>
        </div>
      )}

      <div
        style={{
          margin: '10px 16px 0',
          padding: '12px 14px',
          borderRadius: 12,
          background: '#fff',
          border: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{t('hall.acceptTasks')}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
            {!canToggleAcceptOn && !acceptingTasks
              ? hasFullDayEscortToday
                ? t('hall.acceptBlockedFullDayEscort')
                : hasOpenTripsToday
                  ? t('hall.acceptBlockedOpenTrips')
                  : t('hall.acceptBlockedInspection')
              : t('hall.acceptTasksHint')}
          </div>
        </div>
        <Switch
          checked={acceptingTasks}
          disabled={acceptBusy || (!acceptingTasks && !canToggleAcceptOn)}
          onChange={(v) => void handleAcceptToggle(v)}
        />
      </div>

      <div className="driver-section driver-section--hall">
        <div className="driver-hall-tabs">
          {segments.map((seg) => (
            <button
              key={seg.key}
              type="button"
              className={`driver-hall-tabs__item ${dateRange === seg.key ? 'driver-hall-tabs__item--active' : ''}`}
              onClick={() => setDateRange(seg.key)}
            >
              <span className="driver-hall-tabs__count">
                {seg.key === 'today' ? tabCounts.today : seg.key === 'tomorrow' ? tabCounts.tomorrow : tabCounts.future}
              </span>
              <span className="driver-hall-tabs__label">{seg.label}</span>
            </button>
          ))}
        </div>

        <div className="driver-hall-list">
          {isOffline && <OfflineBanner />}

          <PullToRefresh onRefresh={fetchTrips}>
            {loading ? (
              <div className="driver-page-loading">
                <DotLoading color="primary" />
              </div>
            ) : trips.length === 0 ? (
              <Empty description={emptyDescriptions[dateRange]} className="driver-page-empty" />
            ) : (
              trips.map((trip) => (
                <TaskCard
                  key={trip.id}
                  trip={trip}
                  variant="hall"
                  expanded={expandedTripId === trip.id}
                  onToggleExpand={(e) => {
                    e.stopPropagation();
                    setExpandedTripId((prev) => (prev === trip.id ? null : trip.id));
                  }}
                  onClick={() => navigate(`/task/${trip.id}`)}
                  onSetOff={() => navigate(`/task/${trip.id}`)}
                  setOffBlocked={inspectionReady !== true}
                />
              ))
            )}
          </PullToRefresh>
        </div>
      </div>
    </div>
  );
}

export default TodayTasks;
